import { Component } from '@theme/component';
import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { ThemeEvents } from '@theme/events';
import { requestIdleCallback } from '@theme/utilities';

/**
 * A custom element that displays a cart item components.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} toggleButton - The toggle button element.
 * @property {HTMLElement} content - The content element.
 * @extends {Component<Refs>}
 */
class CartItemDetails extends Component {
  requiredRefs = ['toggleButton', 'content'];
  #expandedClass = 'cart-item-details__content--expanded';
  #isUserExpanded = false;
  #scheduledUpdate = 0;
  #suppressObservers = false;

  /** @type {ResizeObserver} */
  #resizeObserver = new ResizeObserver(() => {
    // Expanding changes height; ignore so we don't collapse to remeasure mid-toggle.
    if (this.#suppressObservers || this.#isUserExpanded) return;
    this.#scheduleTruncationCheck();
  });

  /**
   * Re-sync when header hydration morph resets child classes to server HTML.
   * @type {MutationObserver}
   */
  #displayObserver = new MutationObserver(() => {
    if (this.#suppressObservers) return;
    this.#scheduleTruncationCheck();
  });

  /**
   * Get the cart drawer component.
   * @returns {DialogComponent | null} The cart drawer component.
   */
  get cartDrawer() {
    return this.closest('cart-drawer-component');
  }

  connectedCallback() {
    super.connectedCallback();

    this.refs.toggleButton.addEventListener('click', this.#onToggleClick);
    this.cartDrawer?.addEventListener(DialogOpenEvent.eventName, this.#onDrawerOpen);
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);

    this.#observeContent();
    this.#displayObserver.observe(this, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['class'],
    });

    // Catch opens that happened before this module upgraded, and layout that
    // settles after connect (fonts / drawer animation / section morph).
    if (this.cartDrawer?.refs?.dialog?.open) this.#scheduleTruncationCheck();
    requestIdleCallback(() => this.#scheduleTruncationCheck());
  }

  updatedCallback() {
    super.updatedCallback();
    this.#observeContent();
    this.#scheduleTruncationCheck();
  }

  #observeContent() {
    this.#resizeObserver.disconnect();
    const { content } = this.refs;
    if (content) this.#resizeObserver.observe(content);
  }

  #onToggleClick = () => {
    if (this.refs.toggleButton.classList.contains('hidden')) return;

    this.#isUserExpanded = !this.#isUserExpanded;
    this.#suppressObservers = true;
    this.#applyState();
    this.#displayObserver.takeRecords();
    this.#suppressObservers = false;
  };

  #onDrawerOpen = () => {
    this.#scheduleTruncationCheck();
  };

  #onCartUpdate = () => {
    this.#scheduleTruncationCheck();
  };

  #scheduleTruncationCheck() {
    cancelAnimationFrame(this.#scheduledUpdate);
    this.#scheduledUpdate = requestAnimationFrame(() => {
      requestAnimationFrame(() => this.#updateTruncationState());
    });
  }

  #updateTruncationState() {
    if (!this.isConnected) return;
    if (!this.offsetParent && this.getClientRects().length === 0) return;

    const { content, toggleButton } = this.refs;

    this.#suppressObservers = true;

    // Always measure collapsed. Measuring while expanded reports no overflow and
    // would reset #isUserExpanded (observers re-run after More/Less clicks).
    content.classList.remove(this.#expandedClass);

    const isOverflowing = content.scrollHeight > content.clientHeight + 1;

    toggleButton.classList.toggle('hidden', !isOverflowing);

    if (!isOverflowing) {
      // No truncation needed: always show full content and reset toggle state.
      this.#isUserExpanded = false;
      content.classList.add(this.#expandedClass);
      toggleButton.setAttribute('aria-expanded', 'false');
    } else {
      this.#applyState();
    }

    // Drop mutations from this update so they don't re-enter after unsuppress.
    this.#displayObserver.takeRecords();
    this.#suppressObservers = false;
  }

  #applyState() {
    this.refs.content.classList.toggle(this.#expandedClass, this.#isUserExpanded);
    this.refs.toggleButton.setAttribute('aria-expanded', String(this.#isUserExpanded));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    cancelAnimationFrame(this.#scheduledUpdate);
    this.#resizeObserver.disconnect();
    this.#displayObserver.disconnect();

    this.refs.toggleButton.removeEventListener('click', this.#onToggleClick);
    this.cartDrawer?.removeEventListener(DialogOpenEvent.eventName, this.#onDrawerOpen);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }
}

if (!customElements.get('cart-item-details')) {
  customElements.define('cart-item-details', CartItemDetails);
}
